import { useContext, useMemo } from 'react';
import { Column } from 'react-table';
import { Button, Dropdown, MenuProps, Select, Tag } from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/az';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { StopPropagation } from '@shared/components/stop-propagation';
import { useBackgroundNavigate } from '@shared/hooks';
import { useBranches } from '@modules/branches';
import { SettingsContext } from '@modules/settings';
import { UserCell } from '@modules/users';
import { ICustomsTask } from '../../interfaces';
import { customsTasksQueryKeys } from '../../utils';

dayjs.extend(relativeTime);

export const useCustomsTasksTableColumns = (): Column<ICustomsTask>[] => {
  const navigate = useBackgroundNavigate();
  const settings = useContext(SettingsContext);
  const branches = useBranches();

  return useMemo<Column<ICustomsTask>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }: any) => {
          const items: MenuProps['items'] = [
            {
              key: 'details',
              label: 'Ətraflı bax',
              icon: <Icons.FileSearchOutlined />,
              onClick: () => navigate(`/customs/tasks/${original.id}`),
            },
          ];
          return (
            <StopPropagation>
              <Dropdown menu={{ items }} trigger={['hover']}>
                <Button icon={<Icons.MoreOutlined />} size="small" />
              </Dropdown>
            </StopPropagation>
          );
        },
      },
      { ...nextTableColumns.small, accessor: (r) => r.id, id: customsTasksQueryKeys.id, Header: 'Kod' },
      { ...nextTableColumns.small, accessor: (r) => r.declaration.user.id, id: customsTasksQueryKeys.declaration.user.id, Header: 'M. kodu' },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.declaration.user.name,
        id: customsTasksQueryKeys.declaration.user.name,
        Header: 'Müştəri',
        Cell: ({ row: { original } }: any) => <UserCell id={original.declaration.user.id} title={original.declaration.user.name} />,
      },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.branch.name,
        id: customsTasksQueryKeys.branch.id,
        Header: 'Filial',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select style={{ width: '100%' }} allowClear={true} value={filterValue} onChange={setFilter}>
            {(branches.data || []).map((elem) => (
              <Select.Option key={elem.id} value={elem.id.toString()}>
                {elem.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.small,
        accessor: (r) => r.declaration.country?.name,
        id: customsTasksQueryKeys.declaration.country.id,
        Header: 'Ölkə',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select style={{ width: '100%' }} allowClear={true} value={filterValue} onChange={setFilter}>
            {(settings.countries || []).map((country) => (
              <Select.Option key={country.id} value={country.id.toString()}>
                {country.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.declaration.trackCode,
        id: customsTasksQueryKeys.declaration.trackCode,
        Header: 'İzləmə kodu',
        Cell: ({ cell: { value } }: any) => <Tag>{value}</Tag>,
      },
      { ...nextTableColumns.large, accessor: (r) => r.declaration.updatedBy?.name, id: customsTasksQueryKeys.declaration.updatedBy.id, Header: 'Anbara əlave edən' },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.createdAt,
        id: customsTasksQueryKeys.createdAtHumanized,
        filterable: false,
        sortable: false,
        Cell: ({ cell: { value } }: any) => (value ? dayjs(value).locale('az').fromNow() : ''),
      },
      {
        ...nextTableColumns.small,
        accessor: (r) => r.declaration.weight,
        id: customsTasksQueryKeys.declaration.weight,
        Header: 'Çəkisi',
        Cell: ({ cell: { value } }: any) => (value != null ? `${Number(value).toFixed(2)} kq` : ''),
      },
      { ...nextTableColumns.normal, accessor: (r) => r.declaration.basket?.name, id: customsTasksQueryKeys.declaration.basket.id, Header: 'Səbət' },
      { ...nextTableColumns.smaller, accessor: (r) => r.declaration.quantity, id: customsTasksQueryKeys.declaration.quantity, Header: 'Miqdar' },
      { ...nextTableColumns.normal, accessor: (r) => r.declaration.status.name, id: customsTasksQueryKeys.declaration.status.name, Header: 'Bağ. status' },
      { ...nextTableColumns.normal, accessor: (r) => r.declaration.productType.name, id: customsTasksQueryKeys.declaration.productType.id, Header: 'Məhsul tipi' },
      { ...nextTableColumns.small, accessor: (r) => r.action, id: customsTasksQueryKeys.action, Header: 'Top' },
      {
        ...nextTableColumns.normal,
        accessor: (r) => r.status.name,
        id: customsTasksQueryKeys.status.id,
        Header: 'Task statusu',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select style={{ width: '100%' }} allowClear={true} value={filterValue} onChange={setFilter}>
            <Select.Option value="37">Təsdiq gözləyir</Select.Option>
            <Select.Option value="38">İcra edilib</Select.Option>
          </Select>
        ),
      },
      { ...nextTableColumns.date, accessor: (r) => r.createdAt, id: customsTasksQueryKeys.createdAt, Header: 'Yaradılıb' },
    ],
    [navigate, branches, settings],
  );
};
